import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  FaFilePdf,
  FaFileExcel,
  FaImage,
  FaDownload,
  FaMapMarkerAlt,
  FaEye,
  FaTimes,
} from 'react-icons/fa';
import { essaysData } from '../data/EssaysData';

// ── Marcador personalizado (evita el bug de los íconos rotos de Leaflet en CRA) ──
const makeIcon = (active) =>
  L.divIcon({
    className: 'radix-marker',
    html: `<div style="
      width:${active ? 26 : 20}px;height:${active ? 26 : 20}px;
      background:${active ? '#f8bb59' : '#18776a'};
      border:3px solid #fff;border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      box-shadow:0 2px 6px rgba(0,0,0,.4);
    "></div>`,
    iconSize: [active ? 26 : 20, active ? 26 : 20],
    iconAnchor: [active ? 13 : 10, active ? 26 : 20],
    popupAnchor: [0, active ? -24 : -18],
  });

// ─────────────────────────────── Estilos ───────────────────────────────
const Page = styled.div`
  background: #f9fafc;
  min-height: 100vh;
  padding: 120px 0 60px;
`;

const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 32px;
  @media screen and (max-width: 768px) {
    padding: 0 18px;
  }
`;

const Title = styled.h1`
  color: #04403e;
  font-size: 2.4rem;
  margin-bottom: 0.5rem;
  @media screen and (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.p`
  color: #6a6a6a;
  font-size: 1.05rem;
  margin-bottom: 2rem;
  max-width: 700px;
`;

const MapCard = styled.div`
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  margin-bottom: 2.5rem;

  .leaflet-container {
    height: 480px;
    width: 100%;
    z-index: 1;
    @media screen and (max-width: 768px) {
      height: 340px;
    }
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  align-items: center;
`;

const FilterLabel = styled.span`
  color: #04403e;
  font-weight: 600;
  margin-right: 0.4rem;
`;

const Chip = styled.button`
  border: 1px solid ${({ active }) => (active ? '#18776a' : '#d0d5e2')};
  background: ${({ active }) => (active ? '#18776a' : '#fff')};
  color: ${({ active }) => (active ? '#fff' : '#4b5573')};
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #18776a;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const Card = styled.div`
  background: #fff;
  border: 2px solid ${({ selected }) => (selected ? '#18776a' : 'transparent')};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.12);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #18776a;
  font-size: 1.4rem;
  margin-bottom: 0.75rem;
`;

const CardTitle = styled.h3`
  color: #04403e;
  font-size: 1.15rem;
  margin-bottom: 0.5rem;
`;

const CardDesc = styled.p`
  color: #6a6a6a;
  font-size: 0.9rem;
  flex-grow: 1;
  margin-bottom: 1rem;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

const Tag = styled.span`
  background: #e1e4d5;
  color: #18776a;
  font-size: 0.75rem;
  padding: 0.2rem 0.6rem;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const BtnPrimary = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: #18776a;
  color: #fff;
  border: none;
  padding: 0.55rem;
  border-radius: 7px;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover {
    background: #066a67;
  }
`;

const BtnGhost = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  color: #18776a;
  border: 1px solid #18776a;
  padding: 0.55rem 0.8rem;
  border-radius: 7px;
  font-size: 0.88rem;
  text-decoration: none;
  transition: all 0.2s ease;
  &:hover {
    background: #e1e4d5;
  }
`;

// ── Visor embebido ──
const Viewer = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  margin-bottom: 2.5rem;
  overflow: hidden;
`;

const ViewerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #04403e;
  color: #fff;
`;

const ViewerTitle = styled.h3`
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const ViewerBody = styled.div`
  iframe,
  img {
    width: 100%;
    display: block;
    border: none;
  }
  iframe {
    height: 75vh;
  }
  img {
    max-height: 75vh;
    object-fit: contain;
    background: #f0f0f0;
  }
`;

const NoPreview = styled.div`
  padding: 3rem;
  text-align: center;
  color: #6a6a6a;
  a {
    color: #18776a;
  }
`;

// ─────────────────────────────── Componente ───────────────────────────────
const typeIcon = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'pdf':
      return <FaFilePdf />;
    case 'excel':
      return <FaFileExcel />;
    case 'image':
      return <FaImage />;
    default:
      return <FaFilePdf />;
  }
};

const EnsayosPage = () => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null); // índice del ensayo abierto
  const [cultivo, setCultivo] = useState('Todos');
  const viewerRef = useRef(null);

  const cultivos = useMemo(() => {
    const set = new Set(essaysData.map((e) => e.cultivo).filter(Boolean));
    return ['Todos', ...set];
  }, []);

  const filtered = useMemo(
    () =>
      essaysData
        .map((e, i) => ({ ...e, _index: i }))
        .filter((e) => cultivo === 'Todos' || e.cultivo === cultivo),
    [cultivo]
  );

  // Un pin por lugar: los ensayos con el mismo nombre/provincia se agrupan
  // y el popup lista todos para elegir cuál ver.
  const locations = useMemo(() => {
    const groups = new Map();
    essaysData.forEach((e, i) => {
      if (!e.location) return;
      const key = `${e.location.name}|${e.location.province}`;
      if (!groups.has(key)) groups.set(key, { key, location: e.location, essays: [] });
      groups.get(key).essays.push({ ...e, _index: i });
    });
    return [...groups.values()];
  }, []);

  const open = (index) => {
    setSelected(index);
    setTimeout(() => {
      viewerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const current = selected != null ? essaysData[selected] : null;

  const renderViewer = () => {
    if (!current) return null;
    const src = encodeURI(current.filePath);
    const type = (current.type || '').toLowerCase();
    let body;
    if (type === 'pdf') {
      body = <iframe title={current.title} src={src} />;
    } else if (type === 'image') {
      body = <img src={src} alt={current.title} />;
    } else {
      body = (
        <NoPreview>
          <p>
            {t(
              'essays_no_preview',
              'Este tipo de archivo no se puede previsualizar en el navegador.'
            )}
          </p>
          <a href={src} download>
            {t('download', 'Descargar')} {current.fileName}
          </a>
        </NoPreview>
      );
    }
    return (
      <Viewer ref={viewerRef}>
        <ViewerHeader>
          <ViewerTitle>
            {typeIcon(current.type)} {current.title}
          </ViewerTitle>
          <CloseBtn onClick={() => setSelected(null)} aria-label="Cerrar">
            <FaTimes />
          </CloseBtn>
        </ViewerHeader>
        <ViewerBody>{body}</ViewerBody>
      </Viewer>
    );
  };

  return (
    <Page>
      <Container>
        <Title>{t('essays_title', 'Ensayos')}</Title>
        <Subtitle>
          {t(
            'essays_subtitle',
            'Explorá nuestros ensayos a campo en el mapa y visualizá los informes directamente acá, sin descargar nada.'
          )}
        </Subtitle>

        {/* Mapa */}
        <MapCard>
          <MapContainer
            center={[-38, -63]}
            zoom={4}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {locations.map((g) => (
              <Marker
                key={g.key}
                position={[g.location.lat, g.location.lng]}
                icon={makeIcon(g.essays.some((e) => e._index === selected))}
              >
                <Popup>
                  <strong>
                    {g.location.name}, {g.location.province}
                  </strong>
                  {g.essays.map((e) => (
                    <div key={e._index} style={{ marginTop: 8 }}>
                      {e.title}
                      <br />
                      <button
                        onClick={() => open(e._index)}
                        style={{
                          marginTop: 4,
                          background: '#18776a',
                          color: '#fff',
                          border: 'none',
                          padding: '4px 10px',
                          borderRadius: 5,
                          cursor: 'pointer',
                        }}
                      >
                        {t('essays_view', 'Ver ensayo')}
                      </button>
                    </div>
                  ))}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </MapCard>

        {/* Visor embebido */}
        {renderViewer()}

        {/* Filtros */}
        <Filters>
          <FilterLabel>{t('essays_filter', 'Cultivo')}:</FilterLabel>
          {cultivos.map((c) => (
            <Chip key={c} active={cultivo === c} onClick={() => setCultivo(c)}>
              {c}
            </Chip>
          ))}
        </Filters>

        {/* Lista de ensayos */}
        <Grid>
          {filtered.map((e) => (
            <Card key={e._index} selected={selected === e._index}>
              <CardTop>{typeIcon(e.type)}</CardTop>
              <CardTitle>{e.title}</CardTitle>
              <CardDesc>{e.description}</CardDesc>
              <MetaRow>
                {e.cultivo && <Tag>{e.cultivo}</Tag>}
                {e.campaña && <Tag>{e.campaña}</Tag>}
                {e.location && (
                  <Tag>
                    <FaMapMarkerAlt /> {e.location.name}
                  </Tag>
                )}
              </MetaRow>
              <CardActions>
                <BtnPrimary onClick={() => open(e._index)}>
                  <FaEye /> {t('essays_view', 'Ver')}
                </BtnPrimary>
                <BtnGhost href={encodeURI(e.filePath)} download title={t('download', 'Descargar')}>
                  <FaDownload />
                </BtnGhost>
              </CardActions>
            </Card>
          ))}
        </Grid>
      </Container>
    </Page>
  );
};

export default EnsayosPage;
